/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import CLog, { LogTypes } from '../../ComponentLogger';
import GoogleAnalytics from '../../GoogleAnalytics';
import { IToggle } from './IToggle';
import NodeTouchActionComponent from './NodeTouchActionComponent';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/** An extension of [[ActionComponentBase]] that toggles on spine when target touched. */
@cc._decorator.ccclass
export default class NodeToggleAnimComponent extends NodeTouchActionComponent implements IToggle
{
    /** Spine anim name when toggled on. */
    @property()
    protected toggleOnAnimation: string = '';

    /** Spine anim name when toggled off. */
    @property()
    protected toggleOffAnimation: string = '';

    /** Whether touching the canvas should toggle off. */
    @property()
    protected clickCanvasToDismiss: boolean = true;

    @property(cc.Node)
    protected animationNode: cc.Node = null;

    @property()
    protected hideOnFinish: boolean = false;

    /** Target animation to toggle. */
    protected anim: cc.Animation = null;

    /** @hidden */
    protected onLoad(): void
    {
        super.onLoad();

        this.configureCanvasTouchHandler();

        if (this.animationNode === null) { this.animationNode = this.node; }

        this.anim = this.animationNode.getComponent(cc.Animation);

        if (!this.anim)
        {
            this.enabled = false;
        }
    }

    /** Configures handler to toggle off on canvas touch. */
    private configureCanvasTouchHandler(): void
    {
        if (!this.clickCanvasToDismiss) { return; }

        if (this.clickCanvasToDismiss)
        {
            let canvasTouchAction: NodeTouchActionComponent = this.node.addComponent(NodeTouchActionComponent);
            canvasTouchAction.TargetNode = cc.find('Canvas');
            canvasTouchAction.Action = this.onCanvasTouchEndHandler;
        }
    }

    /** Canvas touch handler. */
    private onCanvasTouchEndHandler = (event: cc.Event.EventTouch) =>
    {
        if (event.target !== this.animationNode) { this.ToggleOff(); }
    }

    /** @hidden */
    private _isOn: boolean = false;

    /** Manually turns off the toggle. */
    public ToggleOff(): void
    {
        this.toggle(false, true);
    }

    /** Manually turns on the toggle. */
    public ToggleOn(): void
    {
        this.toggle(true, true);
    }

    /** @hidden */
    protected action = (): void =>
    {
        this.toggle(!this._isOn);
    }

    /** @hidden */
    protected toggle = (isOn: boolean = false, force: boolean = false): void =>
    {
        if (!this.enabled && !force) { return; }

        if (isOn && !this._isOn)
        {
            this.animationNode.active = true;

            if (this.anim)
            {
                this.anim.play(this.toggleOnAnimation);
            }
            else
            {
                CLog.warn('Null animation.', LogTypes.Utility, this);
            }
            GoogleAnalytics.ToggleEvent(this.node.name, isOn);
        }
        else if (!isOn && this._isOn)
        {
            let animState: cc.AnimationState = this.anim.play(this.toggleOffAnimation);

            this.anim.once('finished', (event) =>
            {
                if (event.currentTarget !== animState) { return; }

                if (this.hideOnFinish) { this.animationNode.active = false; }
            });

            GoogleAnalytics.ToggleEvent(this.node.name, isOn);
        }

        this._isOn = isOn;
    }
}